import React, { useState, useEffect } from 'react';
import GirlsCornerArchive from './GirlsCornerArchive.tsx';
import { AppIcon } from '../../icons/appIcons.tsx';
import EmptyState from '../common/EmptyState.tsx';

interface Photo {
  id: string;
  fileName: string;
  originalFileName: string;
  displayOrder: number;
}

interface Post {
  id: string;
  title: string;
  content: string;
  createdAt: string;
  creator: {
    firstName: string;
    lastName: string;
  };
  photos: Photo[];
}

interface GirlsCornerViewProps {
  compact?: boolean;
}

export default function GirlsCornerView({ compact = false }: GirlsCornerViewProps) {
  const [currentPost, setCurrentPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [showArchive, setShowArchive] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    fetchCurrentPost();
  }, []);

  const fetchCurrentPost = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/girlscorner/current', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success && data.data) {
        setCurrentPost(data.data);
      }
    } catch (error) {
      console.error('Error fetching current post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const nextPhoto = () => {
    if (currentPost && currentPost.photos.length > 0) {
      setCurrentPhotoIndex((prev) => (prev + 1) % currentPost.photos.length);
    }
  };

  const prevPhoto = () => {
    if (currentPost && currentPost.photos.length > 0) {
      setCurrentPhotoIndex((prev) => 
        prev === 0 ? currentPost.photos.length - 1 : prev - 1
      );
    }
  };

  if (showArchive) {
    return <GirlsCornerArchive onClose={() => setShowArchive(false)} />;
  }

  if (compact) {
    // Compact view for dashboard
    return (
      <div className="bg-white rounded-xl shadow-lg border border-ggaa-green/20 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AppIcon name="girlsCorner" className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 shrink-0" />
              <h2 className="text-base sm:text-lg font-semibold">Girls' Corner</h2>
            </div>
            <button
              onClick={() => setShowArchive(true)}
              className="text-xs sm:text-sm hover:underline whitespace-nowrap"
            >
              View Archive →
            </button>
          </div>
        </div>
        
        <div className="p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-6 sm:py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ggaa-green"></div>
            <span className="ml-2 text-sm text-gray-600">Loading...</span>
          </div>
        ) : !currentPost ? (
          <div className="text-center py-6 sm:py-8 text-gray-500">
            <p className="text-xs sm:text-sm">No current post</p>
          </div>
        ) : (
          <div>
            <div className="text-sm text-gray-500 mb-2">{formatDate(currentPost.createdAt)}</div>
            <h4 className="font-bold text-gray-900 mb-2">{currentPost.title}</h4>
            
            {/* Content - show truncated or full based on expanded state */}
            <div className={`text-sm text-gray-700 whitespace-pre-wrap ${isExpanded ? '' : 'line-clamp-4'}`}>
              {currentPost.content}
            </div>

            {/* Photos - show when expanded */}
            {isExpanded && currentPost.photos.length > 0 && (
              <div className="mt-4">
                {currentPost.photos.length === 1 ? (
                  // Single photo
                  <div className="rounded-lg overflow-hidden shadow-md">
                    <img
                      src={`/uploads/girlscorner/${currentPost.photos[0].fileName}`}
                      alt={currentPost.photos[0].originalFileName}
                      className="w-full max-h-64 object-contain bg-gray-100"
                    />
                  </div>
                ) : (
                  // Multiple photos - carousel
                  <div className="relative">
                    <div className="rounded-lg overflow-hidden shadow-md bg-gray-100">
                      <img
                        src={`/uploads/girlscorner/${currentPost.photos[currentPhotoIndex].fileName}`}
                        alt={currentPost.photos[currentPhotoIndex].originalFileName}
                        className="w-full max-h-64 object-contain"
                      />
                    </div>
                    
                    {/* Navigation Arrows */}
                    {currentPost.photos.length > 1 && (
                      <>
                        <button
                          onClick={prevPhoto}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full w-8 h-8 flex items-center justify-center shadow-lg transition-all"
                        >
                          ←
                        </button>
                        <button
                          onClick={nextPhoto}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full w-8 h-8 flex items-center justify-center shadow-lg transition-all"
                        >
                          →
                        </button>
                        
                        {/* Photo Counter */}
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white px-2 py-1 rounded-full text-xs">
                          {currentPhotoIndex + 1} / {currentPost.photos.length}
                        </div>
                      </>
                    )}

                    {/* Thumbnail Navigation */}
                    {currentPost.photos.length > 1 && (
                      <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
                        {currentPost.photos.map((photo, index) => (
                          <button
                            key={photo.id}
                            onClick={() => setCurrentPhotoIndex(index)}
                            className={`flex-shrink-0 w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
                              index === currentPhotoIndex 
                                ? 'border-ggaa-green scale-110' 
                                : 'border-gray-300 opacity-60 hover:opacity-100'
                            }`}
                          >
                            <img
                              src={`/uploads/girlscorner/${photo.fileName}`}
                              alt={photo.originalFileName}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Toggle button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-3 text-ggaa-green hover:text-ggaa-dark text-sm font-medium"
            >
              {isExpanded ? '← Show less' : 'Read more →'}
            </button>
          </div>
        )}
        </div>
      </div>
    );
  }

  // Full view
  return (
    <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-10">
      <div className="mb-6 sm:mb-8 flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
          <AppIcon name="girlsCorner" className="w-8 h-8 sm:w-9 sm:h-9 text-ggaa-green shrink-0" />
          <span>Girls' Corner</span>
        </h1>
        <button
          onClick={() => setShowArchive(true)}
          className="px-4 py-2 text-ggaa-green hover:text-ggaa-dark font-medium text-sm sm:text-base"
        >
          View Past Posts →
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green"></div>
          <span className="ml-4 text-gray-600">Loading...</span>
        </div>
      ) : !currentPost ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <EmptyState icon="girlsCorner" title="No Current Post" description="Check back soon for the latest Girls' Corner update!" className="py-4" />
          <button
            onClick={() => setShowArchive(true)}
            className="text-ggaa-green hover:text-ggaa-dark font-medium"
          >
            View Past Posts
          </button>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-white to-ggaa-cream/30 rounded-xl shadow-xl p-6 sm:p-8 border-2 border-ggaa-green/20">
          {/* Header */}
          <div className="mb-6">
            <div className="text-sm text-gray-500 mb-2">{formatDate(currentPost.createdAt)}</div>
            <h2 className="text-2xl sm:text-3xl font-bold text-ggaa-brown mb-4">{currentPost.title}</h2>
          </div>

          {/* Content */}
          <div className="prose max-w-none mb-6">
            <div className="text-base sm:text-lg text-gray-700 whitespace-pre-wrap leading-relaxed">
              {currentPost.content}
            </div>
          </div>

          {/* Photos */}
          {currentPost.photos.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AppIcon name="camera" className="w-5 h-5" />
                Photos
              </h3>
              
              {currentPost.photos.length === 1 ? (
                // Single photo
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img
                    src={`/uploads/girlscorner/${currentPost.photos[0].fileName}`}
                    alt={currentPost.photos[0].originalFileName}
                    className="w-full max-h-96 object-contain bg-gray-100"
                  />
                </div>
              ) : (
                // Multiple photos - carousel
                <div className="relative">
                  <div className="rounded-lg overflow-hidden shadow-lg bg-gray-100">
                    <img
                      src={`/uploads/girlscorner/${currentPost.photos[currentPhotoIndex].fileName}`}
                      alt={currentPost.photos[currentPhotoIndex].originalFileName}
                      className="w-full max-h-96 object-contain"
                    />
                  </div>
                  
                  {/* Navigation Arrows */}
                  <button
                    onClick={prevPhoto}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-all"
                  >
                    ←
                  </button>
                  <button
                    onClick={nextPhoto}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-all"
                  >
                    →
                  </button>
                  
                  {/* Photo Counter */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                    {currentPhotoIndex + 1} / {currentPost.photos.length}
                  </div>

                  {/* Thumbnail Navigation */}
                  <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                    {currentPost.photos.map((photo, index) => (
                      <button
                        key={photo.id}
                        onClick={() => setCurrentPhotoIndex(index)}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                          index === currentPhotoIndex 
                            ? 'border-ggaa-green scale-110' 
                            : 'border-gray-300 opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={`/uploads/girlscorner/${photo.fileName}`}
                          alt={photo.originalFileName}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

